import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Users,
  ShoppingBag,
  Mail,
  Heart,
  Inbox,
  LogOut,
  Send,
  Eye,
  Gift,
  Activity,
  KeyRound,
  Copy,
  Check,
  RefreshCw,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "Overview", icon: Heart },
  { id: "funnels", label: "Funnels", icon: Activity },
  { id: "sales", label: "Sales", icon: ShoppingBag },
  { id: "starter-kit", label: "Starter Kit", icon: Inbox },
  { id: "newsletter", label: "Newsletter", icon: Mail },
  { id: "waitlist", label: "Waitlists", icon: Users },
  { id: "members", label: "Members", icon: Users },
  { id: "referrals", label: "Referrals", icon: Gift },
  { id: "messages", label: "Messages", icon: Mail },
  { id: "email-log", label: "Email Log", icon: Send },
];

function downloadCsv(filename, rows) {
  if (!rows || rows.length === 0) return;
  const keys = Object.keys(rows[0]);
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const csv = [
    keys.join(","),
    ...rows.map((r) => keys.map((k) => escape(r[k])).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function StatCard({ label, value, sub, accent = "sage" }) {
  return (
    <div className="bg-white border border-hairline rounded-2xl p-6" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`eyebrow mb-2 text-${accent}`}>{label}</div>
      <div className="font-serif-display text-4xl text-ink tabular-nums">{value}</div>
      {sub && <div className="text-xs text-ink-soft mt-2">{sub}</div>}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function fmtMoney(amount, currency = "AUD") {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: (currency || "AUD").toUpperCase(),
  }).format(Number(amount));
}

function Table({ columns, rows, emptyText, testId }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="bg-cream-soft border border-hairline rounded-2xl p-10 text-center text-ink-soft" data-testid={`${testId}-empty`}>
        {emptyText}
      </div>
    );
  }
  return (
    <div className="bg-white border border-hairline rounded-2xl overflow-hidden" data-testid={testId}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-hairline bg-cream-soft">
              {columns.map((c) => (
                <th key={c.key} className="text-left px-5 py-3 font-medium text-ink-soft text-xs uppercase tracking-wider">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-hairline/60 last:border-0 hover:bg-cream-soft/50">
                {columns.map((c) => (
                  <td key={c.key} className="px-5 py-3 text-ink">
                    {c.render ? c.render(r) : r[c.key] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [sales, setSales] = useState(null);
  const [leads, setLeads] = useState(null);
  const [waitlist, setWaitlist] = useState(null);
  const [members, setMembers] = useState(null);
  const [messages, setMessages] = useState(null);
  const [emailLog, setEmailLog] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [newsletter, setNewsletter] = useState(null);
  const [funnels, setFunnels] = useState(null);
  const [loading, setLoading] = useState(true);
  const [digestSending, setDigestSending] = useState(false);
  const [previewEmail, setPreviewEmail] = useState(null);
  // Editor password rotation (Wingman) — discrete admin-only flow.
  const [editorPwModalOpen, setEditorPwModalOpen] = useState(false);
  const [editorPwRotating, setEditorPwRotating] = useState(false);
  const [editorPwResult, setEditorPwResult] = useState(null); // { email, password, generated_at }
  const [editorPwCopied, setEditorPwCopied] = useState(false);

  // Manual password-reset link issuance for locked-out participants.
  const [resetLinkModalOpen, setResetLinkModalOpen] = useState(false);
  const [resetLinkEmail, setResetLinkEmail] = useState("");
  const [resetLinkLoading, setResetLinkLoading] = useState(false);
  const [resetLinkResult, setResetLinkResult] = useState(null);
  const [resetLinkCopied, setResetLinkCopied] = useState(false);
  const [resetLinkError, setResetLinkError] = useState("");

  const issueResetLink = async () => {
    setResetLinkError("");
    setResetLinkLoading(true);
    try {
      const { data } = await api.post("/admin/issue-reset-link", {
        email: resetLinkEmail.trim().toLowerCase(),
      });
      setResetLinkResult(data);
      setResetLinkCopied(false);
    } catch (err) {
      setResetLinkError(
        err?.response?.data?.detail ||
          "Couldn't issue a reset link. Check the email and try again."
      );
    } finally {
      setResetLinkLoading(false);
    }
  };

  const copyResetLink = async () => {
    if (!resetLinkResult?.reset_url) return;
    try {
      await navigator.clipboard.writeText(resetLinkResult.reset_url);
      setResetLinkCopied(true);
      toast.success("Reset link copied to clipboard");
      setTimeout(() => setResetLinkCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — select the link and copy manually.");
    }
  };

  const closeResetLinkModal = () => {
    setResetLinkModalOpen(false);
    setResetLinkResult(null);
    setResetLinkEmail("");
    setResetLinkCopied(false);
    setResetLinkError("");
  };

  const rotateEditorPassword = async () => {
    setEditorPwRotating(true);
    try {
      const { data } = await api.post("/admin/rotate-editor-password");
      setEditorPwResult(data);
      setEditorPwCopied(false);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Couldn't rotate the editor password. Please try again."
      );
      setEditorPwModalOpen(false);
    } finally {
      setEditorPwRotating(false);
    }
  };

  const copyEditorPw = async () => {
    if (!editorPwResult?.password) return;
    try {
      await navigator.clipboard.writeText(editorPwResult.password);
      setEditorPwCopied(true);
      toast.success("Password copied to clipboard");
      setTimeout(() => setEditorPwCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — select the password and copy manually.");
    }
  };

  const closeEditorPwModal = () => {
    // Clear the password from local state immediately so it can't be recovered
    // by re-opening the modal or inspecting React DevTools state.
    setEditorPwModalOpen(false);
    setEditorPwResult(null);
    setEditorPwCopied(false);
  };

  const refreshEmailLog = async () => {
    try {
      const r = await api.get("/admin/email-log");
      setEmailLog(r.data);
    } catch {
      /* ignore */
    }
  };

  const sendDigestNow = async () => {
    setDigestSending(true);
    try {
      const { data } = await api.post("/admin/digest/send-now");
      if (data.ac_dispatched) {
        toast.success(`Digest fired to ActiveCampaign for ${data.to_email}`);
      } else {
        toast.message("Digest captured in log (AC not configured yet)", {
          description: data.ac_error || "Check the Email Log tab to preview.",
        });
      }
      await refreshEmailLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send digest");
    } finally {
      setDigestSending(false);
    }
  };

  const openPreview = async (id) => {
    try {
      const { data } = await api.get(`/admin/email-log/${id}`);
      setPreviewEmail(data);
    } catch {
      toast.error("Couldn't load preview");
    }
  };

  useEffect(() => {
    if (!previewEmail) return;
    const onKey = (e) => {
      if (e.key === "Escape") setPreviewEmail(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [previewEmail]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [s, sa, l, w, m, msg, el, rf, nl, fn] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/sales"),
          api.get("/admin/leads"),
          api.get("/admin/waitlist"),
          api.get("/admin/members"),
          api.get("/admin/messages"),
          api.get("/admin/email-log"),
          api.get("/admin/referrals"),
          api.get("/admin/newsletter"),
          api.get("/admin/funnels"),
        ]);
        if (cancelled) return;
        setStats(s.data);
        setSales(sa.data);
        setLeads(l.data);
        setWaitlist(w.data);
        setMembers(m.data);
        setMessages(msg.data);
        setEmailLog(el.data);
        setReferrals(rf.data);
        setNewsletter(nl.data);
        setFunnels(fn.data);
      } catch (e) {
        // Surface upstream auth errors gracefully
        if (e?.response?.status === 401) {
          navigate("/login");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const mfWaitlist = (waitlist || []).filter((w) =>
    (w.interest || "").toLowerCase().includes("making fabulous"),
  );
  const coachingWaitlist = (waitlist || []).filter(
    (w) => !(w.interest || "").toLowerCase().includes("making fabulous"),
  );

  return (
    <div className="min-h-screen bg-cream" data-testid="page-admin">
      <header className="bg-cream-soft border-b border-hairline sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-serif-display text-xl text-ink">
              Pip<span className="text-gold italic"> Bennett</span>
            </Link>
            <div className="hidden md:block text-xs text-ink-soft uppercase tracking-wider">Admin</div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/admin/insights" className="text-sage hover:text-sage-dark font-medium inline-flex items-center gap-1.5" data-testid="admin-nav-insights">
              The Journal CMS
            </Link>
            <button
              type="button"
              onClick={() => { setEditorPwModalOpen(true); setEditorPwResult(null); }}
              className="text-ink-soft hover:text-ink inline-flex items-center gap-1.5"
              data-testid="admin-rotate-editor-pw"
              title="Generate a fresh temporary password for Wingman"
            >
              <KeyRound size={14} strokeWidth={1.5} /> Rotate editor password
            </button>
            <button
              type="button"
              onClick={() => { setResetLinkModalOpen(true); setResetLinkResult(null); setResetLinkEmail(""); setResetLinkError(""); }}
              className="text-ink-soft hover:text-ink inline-flex items-center gap-1.5"
              data-testid="admin-issue-reset-link"
              title="Manually issue a password-reset link for any participant"
            >
              <Mail size={14} strokeWidth={1.5} /> Issue reset link
            </button>
            <Link to="/dashboard" className="text-ink-soft hover:text-ink inline-flex items-center gap-1.5" data-testid="admin-nav-dashboard">
              <ArrowLeft size={14} strokeWidth={1.5} /> Dashboard
            </Link>
            <button onClick={handleLogout} className="text-ink-soft hover:text-ink inline-flex items-center gap-1.5" data-testid="admin-logout">
              <LogOut size={14} strokeWidth={1.5} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="eyebrow mb-3 text-sage">Admin Console</div>
        <h1 className="font-serif-display text-4xl md:text-6xl text-ink leading-[1.05]" data-testid="admin-title">
          Hello, <em className="italic text-sage">{user?.name || "Pip"}</em>.
        </h1>
        <p className="mt-4 text-ink-soft max-w-xl">
          Everything humming through the site, all in one place.
        </p>

        {/* Tabs */}
        <div className="mt-10 flex flex-wrap gap-2 border-b border-hairline">
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? "border-sage text-sage"
                    : "border-transparent text-ink-soft hover:text-ink"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                <t.icon size={14} strokeWidth={1.5} />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-10">
          {loading && <div className="text-ink-soft">Loading the numbers…</div>}

          {!loading && tab === "overview" && stats && (
            <div className="space-y-8" data-testid="admin-overview">
              <div className="flex items-center justify-end -mb-4">
                <button
                  onClick={sendDigestNow}
                  disabled={digestSending}
                  className="inline-flex items-center gap-2 text-sm bg-sage hover:bg-sage-dark text-cream rounded-full px-5 py-2.5 transition-all disabled:opacity-50"
                  data-testid="admin-digest-send-now"
                >
                  <Send size={14} strokeWidth={1.5} />
                  {digestSending ? "Sending…" : "Send weekly digest now"}
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  label="Total Revenue"
                  value={fmtMoney(stats.sales.total_revenue)}
                  sub={`${stats.sales.total_paid_count} paid orders`}
                />
                <StatCard
                  label="Members"
                  value={stats.members.total}
                  sub={`${stats.members.mf_customers} MF · ${stats.members.tmi_customers} TMI`}
                  accent="gold"
                />
                <StatCard
                  label="Starter Kit"
                  value={stats.starter_kit.total}
                  sub={`${stats.starter_kit.last_7d} in the last 7 days`}
                />
                <StatCard
                  label="Messages"
                  value={stats.messages.total}
                  sub={`${stats.messages.last_7d} in the last 7 days`}
                  accent="gold"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-sage text-cream rounded-2xl p-7" data-testid="admin-overview-mf">
                  <div className="eyebrow mb-3 text-gold">The Rewired Life</div>
                  <div className="font-serif-display text-3xl">
                    {fmtMoney(stats.sales.mf_revenue)}
                    <span className="text-cream/60 text-base ml-2">{stats.sales.mf_paid_count} sold</span>
                  </div>
                  <div className="mt-2 text-cream/70 text-sm">
                    {stats.waitlist.mf} on the waitlist · pre-launch
                  </div>
                </div>
                <div className="bg-peach text-ink rounded-2xl p-7" data-testid="admin-overview-tmi">
                  <div className="eyebrow mb-3 text-sage">The Menopause Invitation</div>
                  <div className="font-serif-display text-3xl">
                    {fmtMoney(stats.sales.tmi_revenue)}
                    <span className="text-ink-soft text-base ml-2">{stats.sales.tmi_paid_count} sold</span>
                  </div>
                  <div className="mt-2 text-ink-soft text-sm">
                    {stats.members.tmi_customers} active members · live
                  </div>
                </div>
              </div>

              <div className="bg-cream-soft border border-hairline rounded-2xl p-7" data-testid="admin-overview-waitlists">
                <div className="eyebrow mb-3 text-sage">Waitlists</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-ink">
                  <div>
                    <div className="font-serif-display text-3xl">{stats.waitlist.mf}</div>
                    <div className="text-sm text-ink-soft">The Rewired Life</div>
                  </div>
                  <div>
                    <div className="font-serif-display text-3xl">{stats.waitlist.coaching}</div>
                    <div className="text-sm text-ink-soft">1:1 coaching</div>
                  </div>
                  <div>
                    <div className="font-serif-display text-3xl">{stats.waitlist.total}</div>
                    <div className="text-sm text-ink-soft">Total</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && tab === "sales" && (
            <SectionWithExport
              title="Paid Orders"
              data={sales}
              filename={`pipbennett-sales-${new Date().toISOString().slice(0, 10)}.csv`}
              testId="admin-sales-section"
              table={
                <Table
                  testId="admin-sales-table"
                  emptyText="No sales yet."
                  rows={sales || []}
                  columns={[
                    {
                      key: "package_id",
                      label: "Program",
                      render: (r) => (r.package_id === "menopause_invitation" ? "TMI" : "MF"),
                    },
                    { key: "user_email", label: "Email" },
                    {
                      key: "amount",
                      label: "Amount",
                      render: (r) => fmtMoney(r.amount, r.currency),
                    },
                    {
                      key: "updated_at",
                      label: "Paid",
                      render: (r) => fmtDate(r.updated_at || r.created_at),
                    },
                  ]}
                />
              }
            />
          )}

          {!loading && tab === "funnels" && funnels && (
            <FunnelsView data={funnels} />
          )}

          {!loading && tab === "starter-kit" && (
            <SectionWithExport
              title="Starter Kit signups"
              data={leads}
              filename={`pipbennett-starterkit-${new Date().toISOString().slice(0, 10)}.csv`}
              testId="admin-leads-section"
              table={
                <Table
                  testId="admin-leads-table"
                  emptyText="No signups yet."
                  rows={leads || []}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    { key: "created_at", label: "Joined", render: (r) => fmtDate(r.created_at) },
                  ]}
                />
              }
            />
          )}

          {!loading && tab === "newsletter" && (
            <SectionWithExport
              title="Sunday with Pip newsletter"
              data={newsletter}
              filename={`pipbennett-newsletter-${new Date().toISOString().slice(0, 10)}.csv`}
              testId="admin-newsletter-section"
              table={
                <Table
                  testId="admin-newsletter-table"
                  emptyText="No newsletter subscribers yet."
                  rows={newsletter || []}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    { key: "created_at", label: "Subscribed", render: (r) => fmtDate(r.created_at) },
                  ]}
                />
              }
            />
          )}

          {!loading && tab === "waitlist" && (
            <div className="space-y-10" data-testid="admin-waitlist-section">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-serif-display text-2xl text-ink">The Rewired Life waitlist</h2>
                  <button
                    onClick={() =>
                      downloadCsv(
                        `pipbennett-mf-waitlist-${new Date().toISOString().slice(0, 10)}.csv`,
                        mfWaitlist,
                      )
                    }
                    className="inline-flex items-center gap-2 text-sm bg-sage hover:bg-sage-dark text-cream rounded-full px-4 py-2 transition-all disabled:opacity-50"
                    disabled={mfWaitlist.length === 0}
                    data-testid="admin-mf-waitlist-export"
                  >
                    <Download size={14} strokeWidth={1.5} /> Export CSV
                  </button>
                </div>
                <Table
                  testId="admin-mf-waitlist-table"
                  emptyText="Nobody on the MF waitlist yet."
                  rows={mfWaitlist}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    { key: "created_at", label: "Joined", render: (r) => fmtDate(r.created_at) },
                  ]}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-serif-display text-2xl text-ink">1:1 coaching waitlist</h2>
                  <button
                    onClick={() =>
                      downloadCsv(
                        `pipbennett-coaching-waitlist-${new Date().toISOString().slice(0, 10)}.csv`,
                        coachingWaitlist,
                      )
                    }
                    className="inline-flex items-center gap-2 text-sm bg-sage hover:bg-sage-dark text-cream rounded-full px-4 py-2 transition-all disabled:opacity-50"
                    disabled={coachingWaitlist.length === 0}
                    data-testid="admin-coaching-waitlist-export"
                  >
                    <Download size={14} strokeWidth={1.5} /> Export CSV
                  </button>
                </div>
                <Table
                  testId="admin-coaching-waitlist-table"
                  emptyText="Nobody on the 1:1 waitlist yet."
                  rows={coachingWaitlist}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    { key: "interest", label: "Interest" },
                    { key: "created_at", label: "Joined", render: (r) => fmtDate(r.created_at) },
                  ]}
                />
              </div>
            </div>
          )}

          {!loading && tab === "members" && (
            <SectionWithExport
              title="Members"
              data={members}
              filename={`pipbennett-members-${new Date().toISOString().slice(0, 10)}.csv`}
              testId="admin-members-section"
              table={
                <Table
                  testId="admin-members-table"
                  emptyText="No members yet."
                  rows={members || []}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    {
                      key: "access",
                      label: "Access",
                      render: (r) => {
                        const has = [];
                        if (r.has_making_fabulous_access) has.push("MF");
                        if (r.has_menopause_invitation_access) has.push("TMI");
                        return has.length ? has.join(" · ") : "—";
                      },
                    },
                    { key: "created_at", label: "Registered", render: (r) => fmtDate(r.created_at) },
                  ]}
                />
              }
            />
          )}

          {!loading && tab === "referrals" && (
            <SectionWithExport
              title="Referral activity"
              data={referrals}
              filename={`pipbennett-referrals-${new Date().toISOString().slice(0, 10)}.csv`}
              testId="admin-referrals-section"
              table={
                <Table
                  testId="admin-referrals-table"
                  emptyText="No referrals yet — once a paid customer's link drives a sale, it'll show here."
                  rows={referrals || []}
                  columns={[
                    { key: "name", label: "Member" },
                    { key: "email", label: "Email" },
                    { key: "referral_code", label: "Code" },
                    {
                      key: "referral_credit_aud",
                      label: "Credit",
                      render: (r) => fmtMoney(r.referral_credit_aud || 0),
                    },
                    {
                      key: "referral_count",
                      label: "Friends joined",
                      render: (r) => (r.referrals || []).length,
                    },
                  ]}
                />
              }
            />
          )}

          {!loading && tab === "messages" && (
            <SectionWithExport
              title="Contact messages"
              data={messages}
              filename={`pipbennett-messages-${new Date().toISOString().slice(0, 10)}.csv`}
              testId="admin-messages-section"
              table={
                <Table
                  testId="admin-messages-table"
                  emptyText="No messages yet."
                  rows={messages || []}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    {
                      key: "message",
                      label: "Message",
                      render: (r) => (
                        <span className="line-clamp-2 max-w-md inline-block">{r.message}</span>
                      ),
                    },
                    { key: "created_at", label: "Sent", render: (r) => fmtDate(r.created_at) },
                  ]}
                />
              }
            />
          )}

          {!loading && tab === "email-log" && (
            <div data-testid="admin-email-log-section">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-serif-display text-2xl text-ink">Email Log</h2>
                  <p className="mt-1 text-sm text-ink-soft max-w-2xl">
                    Every digest, receipt, and triggered email is captured here. The body shown below is
                    exactly what was prepared — when ActiveCampaign automations are wired up, that's also
                    what subscribers receive.
                  </p>
                </div>
                <button
                  onClick={refreshEmailLog}
                  className="inline-flex items-center gap-2 text-sm border border-hairline hover:border-sage rounded-full px-4 py-2"
                  data-testid="admin-email-log-refresh"
                >
                  Refresh
                </button>
              </div>
              <Table
                testId="admin-email-log-table"
                emptyText="No emails fired yet."
                rows={emailLog || []}
                columns={[
                  {
                    key: "kind",
                    label: "Kind",
                    render: (r) => (
                      <span className="inline-flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${r.ac_dispatched ? "bg-sage" : "bg-gold"}`} />
                        {r.kind}
                      </span>
                    ),
                  },
                  { key: "to_email", label: "To" },
                  {
                    key: "subject",
                    label: "Subject",
                    render: (r) => <span className="line-clamp-1 max-w-md inline-block">{r.subject}</span>,
                  },
                  {
                    key: "ac_dispatched",
                    label: "AC",
                    render: (r) => (r.ac_dispatched ? "✓ Sent" : "Draft"),
                  },
                  { key: "created_at", label: "When", render: (r) => fmtDate(r.created_at) },
                  {
                    key: "preview",
                    label: "",
                    render: (r) => (
                      <button
                        onClick={() => openPreview(r.id)}
                        className="inline-flex items-center gap-1.5 text-sage hover:text-sage-dark text-xs"
                        data-testid={`admin-email-log-preview-${r.id}`}
                      >
                        <Eye size={12} strokeWidth={1.5} /> Preview
                      </button>
                    ),
                  },
                ]}
              />
              <div className="mt-6 bg-cream-soft border border-hairline rounded-2xl p-6 text-sm text-ink-soft" data-testid="admin-email-log-help">
                <div className="eyebrow text-sage mb-2">How this works</div>
                <p className="leading-relaxed">
                  This panel captures every email the site needs to send. Currently each email <em>also</em> applies
                  a trigger tag in ActiveCampaign so your AC automation can do the actual sending. To activate,
                  set up these automations in ActiveCampaign with a <strong>"Tag is added"</strong> trigger:
                </p>
                <ul className="mt-3 list-disc pl-5 space-y-1.5">
                  <li><code className="bg-white px-1.5 py-0.5 rounded text-xs">trigger · admin digest</code> — sends the weekly digest body to you</li>
                  <li><code className="bg-white px-1.5 py-0.5 rounded text-xs">trigger · tmi order confirmation</code> — sends the TMI receipt</li>
                  <li><code className="bg-white px-1.5 py-0.5 rounded text-xs">trigger · mf order confirmation</code> — sends the MF receipt (when MF reopens)</li>
                </ul>
                <p className="mt-3 leading-relaxed">
                  Until the automations are configured, emails are captured here in draft mode — nothing is lost.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {previewEmail && (
        <div
          className="fixed inset-0 z-[80] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setPreviewEmail(null)}
          data-testid="admin-email-preview-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-hairline px-6 py-4 flex items-center justify-between">
              <div>
                <div className="eyebrow text-sage">{previewEmail.kind}</div>
                <div className="font-serif-display text-xl text-ink mt-1 truncate max-w-md">{previewEmail.subject}</div>
                <div className="text-xs text-ink-soft mt-1">
                  To {previewEmail.to_email} · {previewEmail.ac_dispatched ? "AC dispatched ✓" : "Draft mode"}
                </div>
              </div>
              <button
                onClick={() => setPreviewEmail(null)}
                className="text-ink-soft hover:text-ink"
                data-testid="admin-email-preview-close"
              >
                ✕
              </button>
            </div>
            <iframe
              title="Email preview"
              className="flex-1 w-full bg-white"
              srcDoc={previewEmail.html_body}
              sandbox=""
              data-testid="admin-email-preview-iframe"
            />
          </div>
        </div>
      )}

      {/* ─── Editor password rotation modal ───────────────────────────────────
          Admin-only. Hardcoded to wings@emergentagent.com on the server side.
          The new password is shown ONCE here; closing the modal clears it from
          local state so it can't be re-retrieved without a fresh rotation. */}
      {editorPwModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 bg-ink/60 backdrop-blur-sm overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rotate-editor-pw-title"
          data-testid="rotate-editor-pw-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget && !editorPwRotating) closeEditorPwModal();
          }}
        >
          <div className="relative bg-cream rounded-3xl max-w-lg w-full p-7 md:p-8 border-2 border-sage/30 shadow-[0_30px_80px_-15px_rgba(0,0,0,0.35)] my-auto">
            <button
              type="button"
              onClick={closeEditorPwModal}
              className="absolute top-5 right-5 text-ink-soft hover:text-ink transition"
              aria-label="Close"
              data-testid="rotate-editor-pw-close"
              disabled={editorPwRotating}
            >
              <X size={20} strokeWidth={1.8} />
            </button>

            <div className="inline-flex items-center gap-2 text-[0.65rem] tracking-[0.18em] uppercase font-bold text-sage-dark mb-3">
              <KeyRound size={11} strokeWidth={2.4} /> Editor access
            </div>
            <h2
              id="rotate-editor-pw-title"
              className="font-serif-display text-2xl md:text-3xl text-ink leading-snug mb-2"
            >
              Rotate Wingman&apos;s password
            </h2>
            <p className="text-sm text-ink-soft leading-relaxed">
              Generates a fresh temporary password for{" "}
              <span className="font-mono text-xs bg-cream-soft border border-hairline rounded px-1.5 py-0.5">
                wings@emergentagent.com
              </span>
              . The new password is shown <strong className="text-ink">once</strong> — copy it and
              send it through your secure channel. Wingman will be forced to set their own password
              the moment they sign in.
            </p>

            {!editorPwResult ? (
              <div className="mt-6 space-y-3">
                <div className="bg-cream-soft border border-hairline rounded-2xl p-4 text-xs text-ink-soft leading-relaxed">
                  <strong className="text-ink not-italic">What this does:</strong>
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    <li>Generates a strong random password</li>
                    <li>Replaces the current editor password immediately</li>
                    <li>Forces Wingman to set their own on next sign-in</li>
                    <li>Does <strong className="text-ink not-italic">not</strong> change the editor role — Journal CMS draft access only</li>
                  </ul>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={closeEditorPwModal}
                    disabled={editorPwRotating}
                    className="flex-1 inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-medium text-ink-soft hover:text-ink border border-hairline hover:border-ink-soft transition disabled:opacity-50"
                    data-testid="rotate-editor-pw-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={rotateEditorPassword}
                    disabled={editorPwRotating}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold bg-sage hover:bg-sage-dark text-cream disabled:opacity-50 transition"
                    data-testid="rotate-editor-pw-generate"
                  >
                    {editorPwRotating ? (
                      <>
                        <RefreshCw size={14} strokeWidth={2} className="animate-spin" />
                        Generating…
                      </>
                    ) : (
                      <>
                        <RefreshCw size={14} strokeWidth={2} />
                        Generate new password
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-6 space-y-4" data-testid="rotate-editor-pw-result">
                <div className="bg-sage/10 border-2 border-sage/30 rounded-2xl p-5">
                  <div className="text-[0.65rem] tracking-[0.18em] uppercase font-bold text-sage-dark mb-2">
                    New temporary password
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <code
                      className="flex-1 min-w-0 bg-white border border-hairline rounded-xl px-4 py-3 text-base md:text-lg font-mono text-ink break-all"
                      data-testid="rotate-editor-pw-value"
                    >
                      {editorPwResult.password}
                    </code>
                    <button
                      type="button"
                      onClick={copyEditorPw}
                      className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-5 py-2.5 text-sm font-medium transition whitespace-nowrap"
                      data-testid="rotate-editor-pw-copy"
                    >
                      {editorPwCopied ? (
                        <>
                          <Check size={14} strokeWidth={2.2} />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy size={14} strokeWidth={1.8} />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex items-start gap-2 bg-gold/10 border border-gold/40 rounded-xl px-4 py-3 text-xs text-ink leading-relaxed">
                  <KeyRound size={13} strokeWidth={2} className="text-gold-dark flex-shrink-0 mt-0.5" />
                  <span>
                    This password will not be shown again. Send it to Wingman through a secure
                    channel (Signal disappearing message, password manager share, or SMS separate
                    from the login URL). They&apos;ll be prompted to change it immediately on first
                    sign-in.
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={() => { setEditorPwResult(null); setEditorPwCopied(false); }}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium border border-hairline text-ink-soft hover:text-ink hover:border-ink-soft transition"
                    data-testid="rotate-editor-pw-again"
                  >
                    <RefreshCw size={14} strokeWidth={1.8} /> Generate another
                  </button>
                  <button
                    type="button"
                    onClick={closeEditorPwModal}
                    className="flex-1 inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-semibold bg-ink text-cream hover:bg-ink/90 transition"
                    data-testid="rotate-editor-pw-done"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Manual password reset link issuance modal ────────────────────────
          Admin enters a participant's email, endpoint generates a fresh reset
          token, modal displays the direct link for the admin to hand-deliver. */}
      {resetLinkModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 bg-ink/60 backdrop-blur-sm overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reset-link-title"
          data-testid="issue-reset-link-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget && !resetLinkLoading) closeResetLinkModal();
          }}
        >
          <div className="relative bg-cream rounded-3xl max-w-lg w-full p-7 md:p-8 border-2 border-sage/30 shadow-[0_30px_80px_-15px_rgba(0,0,0,0.35)] my-auto">
            <button
              type="button"
              onClick={closeResetLinkModal}
              className="absolute top-5 right-5 text-ink-soft hover:text-ink transition"
              aria-label="Close"
              data-testid="issue-reset-link-close"
              disabled={resetLinkLoading}
            >
              <X size={20} strokeWidth={1.8} />
            </button>

            <div className="inline-flex items-center gap-2 text-[0.65rem] tracking-[0.18em] uppercase font-bold text-sage-dark mb-3">
              <Mail size={11} strokeWidth={2.4} /> Manual password reset
            </div>
            <h2
              id="reset-link-title"
              className="font-serif-display text-2xl md:text-3xl text-ink leading-snug mb-2"
            >
              Issue a reset link
            </h2>
            <p className="text-sm text-ink-soft leading-relaxed">
              Generate a one-time reset link for a participant who hasn&apos;t received the
              automated email. Link is valid for <strong className="text-ink">24 hours</strong>.
              Any prior unused reset tokens for this email will be invalidated.
            </p>

            {!resetLinkResult ? (
              <div className="mt-6 space-y-3">
                <label className="eyebrow block text-ink-soft">Participant email</label>
                <input
                  type="email"
                  value={resetLinkEmail}
                  onChange={(e) => setResetLinkEmail(e.target.value)}
                  disabled={resetLinkLoading}
                  autoFocus
                  placeholder="participant@example.com"
                  className="w-full bg-white border border-hairline rounded-full px-6 py-3.5 text-ink focus:outline-none focus:border-sage transition"
                  data-testid="issue-reset-link-email"
                />
                {resetLinkError && (
                  <div
                    className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3"
                    data-testid="issue-reset-link-error"
                  >
                    {resetLinkError}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <button
                    type="button"
                    onClick={closeResetLinkModal}
                    disabled={resetLinkLoading}
                    className="flex-1 inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-medium text-ink-soft hover:text-ink border border-hairline hover:border-ink-soft transition disabled:opacity-50"
                    data-testid="issue-reset-link-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={issueResetLink}
                    disabled={resetLinkLoading || !resetLinkEmail.includes("@")}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold bg-sage hover:bg-sage-dark text-cream disabled:opacity-50 transition"
                    data-testid="issue-reset-link-generate"
                  >
                    {resetLinkLoading ? "Generating…" : "Generate link"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-6 space-y-4" data-testid="issue-reset-link-result">
                <div className="bg-sage/10 border-2 border-sage/30 rounded-2xl p-5">
                  <div className="text-[0.65rem] tracking-[0.18em] uppercase font-bold text-sage-dark mb-1">
                    Reset link for {resetLinkResult.name || resetLinkResult.email}
                  </div>
                  <div className="text-xs text-ink-soft mb-3">
                    Expires in {resetLinkResult.expires_in_hours} hours
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <code
                      className="flex-1 min-w-0 bg-white border border-hairline rounded-xl px-4 py-3 text-xs md:text-sm font-mono text-ink break-all"
                      data-testid="issue-reset-link-value"
                    >
                      {resetLinkResult.reset_url}
                    </code>
                    <button
                      type="button"
                      onClick={copyResetLink}
                      className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-5 py-2.5 text-sm font-medium transition whitespace-nowrap"
                      data-testid="issue-reset-link-copy"
                    >
                      {resetLinkCopied ? (
                        <>
                          <Check size={14} strokeWidth={2.2} /> Copied
                        </>
                      ) : (
                        <>
                          <Copy size={14} strokeWidth={1.8} /> Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex items-start gap-2 bg-gold/10 border border-gold/40 rounded-xl px-4 py-3 text-xs text-ink leading-relaxed">
                  <Mail size={13} strokeWidth={2} className="text-gold-dark flex-shrink-0 mt-0.5" />
                  <span>
                    Send this link directly to the participant via your secure channel. When they
                    open it, they&apos;ll set their own new password and be signed in immediately.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={closeResetLinkModal}
                  className="w-full inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-semibold bg-ink text-cream hover:bg-ink/90 transition"
                  data-testid="issue-reset-link-done"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionWithExport({ title, data, filename, table, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif-display text-2xl text-ink">{title}</h2>
        <button
          onClick={() => downloadCsv(filename, data || [])}
          className="inline-flex items-center gap-2 text-sm bg-sage hover:bg-sage-dark text-cream rounded-full px-4 py-2 transition-all disabled:opacity-50"
          disabled={!data || data.length === 0}
          data-testid={`${testId}-export`}
        >
          <Download size={14} strokeWidth={1.5} /> Export CSV
        </button>
      </div>
      {table}
    </div>
  );
}

function FunnelsView({ data }) {
  const [windowKey, setWindowKey] = useState("last_7d");
  const windowLabels = {
    last_7d: "Last 7 days",
    last_30d: "Last 30 days",
    all_time: "All time",
  };

  const trackingStarted = data.tracking_started_at
    ? new Date(data.tracking_started_at)
    : null;

  return (
    <div className="space-y-8" data-testid="admin-funnels">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h2 className="font-serif-display text-3xl text-ink">
            Conversion <em className="italic text-sage">funnels</em>
          </h2>
          <p className="mt-2 text-ink-soft text-sm max-w-xl">
            Where people land vs. where they convert. Unique visitors per day —
            so a single person refreshing five times still counts as one.
          </p>
        </div>
        <div className="inline-flex items-center bg-cream-soft border border-hairline rounded-full p-1">
          {Object.entries(windowLabels).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setWindowKey(k)}
              data-testid={`funnel-window-${k}`}
              className={`px-4 py-2 text-xs font-medium rounded-full transition-colors ${
                windowKey === k
                  ? "bg-sage text-cream"
                  : "text-ink-soft hover:text-ink"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {trackingStarted && (
        <div className="text-xs text-ink-soft/70 italic">
          Visitor tracking began{" "}
          {trackingStarted.toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
          . Conversions from before that date are still counted, which can make
          early "all time" rates look high.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {data.funnels.map((f) => (
          <FunnelCard
            key={f.id}
            funnel={f}
            windowKey={windowKey}
            referrers={(data.referrers_by_funnel || {})[f.id] || []}
          />
        ))}
      </div>

      {data.mens_guide_attribution && (
        <MensGuideAttribution
          data={data.mens_guide_attribution}
          windowKey={windowKey}
        />
      )}

      {data.mens_guide_shares && (
        <MensGuideShares data={data.mens_guide_shares} />
      )}

      {data.top_referrers_30d && data.top_referrers_30d.length > 0 && (
        <div
          className="bg-cream-soft border border-hairline rounded-2xl p-6 md:p-8"
          data-testid="admin-referrers"
        >
          <div className="flex items-baseline justify-between">
            <div>
              <h3 className="font-serif-display text-2xl text-ink">
                Where they're <em className="italic text-sage">coming from</em>
              </h3>
              <p className="text-xs text-ink-soft/70 mt-1">
                External traffic sources · last 30 days
              </p>
            </div>
          </div>
          <ul className="mt-6 divide-y divide-hairline">
            {data.top_referrers_30d.map((r) => (
              <li
                key={r.source}
                className="flex items-center justify-between py-3"
              >
                <span className="text-ink">{r.source}</span>
                <span className="text-sm text-ink-soft tabular-nums">
                  {r.visits.toLocaleString()} visits
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function FunnelCard({ funnel, windowKey, referrers = [] }) {
  const w = funnel.windows[windowKey];
  const visitors = w.visitors;
  const conversions = w.conversions;
  const rate = w.rate;
  // Drop-off bar: visitor bar is full width, conversion bar is rate%
  const conversionWidth = Math.max(Math.min(rate, 100), conversions > 0 ? 4 : 0);

  return (
    <div
      className="bg-cream border border-hairline rounded-2xl p-6 md:p-7"
      data-testid={`funnel-card-${funnel.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow text-sage">
            {funnel.path === "/" ? "Homepage" : funnel.path}
          </div>
          <h3 className="mt-1 font-serif-display text-xl text-ink leading-snug">
            {funnel.label}
          </h3>
        </div>
        <div
          className="text-right shrink-0"
          data-testid={`funnel-rate-${funnel.id}`}
        >
          <div className="font-serif-display text-3xl text-ink tabular-nums">
            {rate.toFixed(1)}%
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft/60">
            conversion
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        <FunnelBar
          label="Visitors"
          value={visitors}
          widthPct={visitors > 0 ? 100 : 0}
          tone="visitor"
        />
        <FunnelBar
          label={funnel.conversion_label}
          value={conversions}
          widthPct={conversionWidth}
          tone="conversion"
        />
      </div>

      {visitors === 0 && conversions === 0 && (
        <p className="mt-5 text-xs text-ink-soft/60 italic">
          No data in this window yet.
        </p>
      )}

      {referrers.length > 0 && (
        <div
          className="mt-6 pt-5 border-t border-hairline"
          data-testid={`funnel-referrers-${funnel.id}`}
        >
          <div className="eyebrow text-ink-soft/70 mb-3 text-[10px]">
            Where they're coming from · last 30d
          </div>
          <ul className="space-y-1.5">
            {referrers.slice(0, 5).map((r) => (
              <li
                key={r.source}
                className="flex items-center justify-between text-xs"
              >
                <span className="text-ink-soft truncate pr-3">{r.source}</span>
                <span className="text-ink tabular-nums shrink-0">
                  {r.visits.toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function MensGuideAttribution({ data, windowKey }) {
  const w = data[windowKey];
  if (!w) return null;
  const {
    sample_watches = 0,
    purchases = 0,
    sample_to_purchase = 0,
    direct_purchases = 0,
    sample_no_buy = 0,
    sample_buy_rate = 0,
    sample_attribution_of_sales = 0,
  } = w;

  // Don't render if there's nothing to show yet
  if (sample_watches === 0 && purchases === 0) return null;

  return (
    <div
      className="bg-sage/[0.06] border border-sage/25 rounded-2xl p-6 md:p-8"
      data-testid="admin-mens-guide-attribution"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-serif-display text-2xl text-ink">
            Men's Guide · <em className="italic text-sage">sample-to-sale</em>
          </h3>
          <p className="text-xs text-ink-soft/70 mt-1">
            Are the free sample watchers actually converting?
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <AttributionStat
          label="Sample watches"
          value={sample_watches}
          testid="mg-attr-samples"
        />
        <AttributionStat
          label="Total purchases"
          value={purchases}
          testid="mg-attr-purchases"
        />
        <AttributionStat
          label="Sample → bought"
          value={sample_to_purchase}
          highlight
          testid="mg-attr-sample-converted"
        />
        <AttributionStat
          label="Bought without sample"
          value={direct_purchases}
          testid="mg-attr-direct"
        />
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div className="bg-cream border border-hairline rounded-xl px-4 py-3 flex items-baseline justify-between">
          <span className="text-ink-soft">Of sample watchers, % who bought</span>
          <span className="font-serif-display text-xl text-sage tabular-nums">
            {sample_buy_rate.toFixed(1)}%
          </span>
        </div>
        <div className="bg-cream border border-hairline rounded-xl px-4 py-3 flex items-baseline justify-between">
          <span className="text-ink-soft">Of buyers, % who took the sample first</span>
          <span className="font-serif-display text-xl text-sage tabular-nums">
            {sample_attribution_of_sales.toFixed(1)}%
          </span>
        </div>
      </div>

      {sample_no_buy > 0 && (
        <p className="mt-5 text-xs text-ink-soft/70">
          <strong className="text-ink not-italic">{sample_no_buy}</strong>{" "}
          {sample_no_buy === 1 ? "person has" : "people have"} watched the sample
          but haven't purchased yet — warm leads ready for a Flodesk nudge.
        </p>
      )}
    </div>
  );
}

function AttributionStat({ label, value, highlight = false, testid }) {
  return (
    <div
      className={`rounded-xl p-4 ${
        highlight
          ? "bg-sage text-cream"
          : "bg-cream border border-hairline"
      }`}
      data-testid={testid}
    >
      <div
        className={`font-serif-display text-3xl tabular-nums ${
          highlight ? "" : "text-ink"
        }`}
      >
        {value.toLocaleString()}
      </div>
      <div
        className={`mt-1 text-[10px] uppercase tracking-[0.16em] ${
          highlight ? "text-cream/80" : "text-ink-soft/70"
        }`}
      >
        {label}
      </div>
    </div>
  );
}

function FunnelBar({ label, value, widthPct, tone }) {
  const fillColor =
    tone === "visitor" ? "bg-sage/30" : "bg-sage";
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-xs uppercase tracking-[0.16em] text-ink-soft/70">
          {label}
        </span>
        <span className="font-serif-display text-lg text-ink tabular-nums">
          {value.toLocaleString()}
        </span>
      </div>
      <div className="w-full h-2 bg-cream-soft rounded-full overflow-hidden">
        <div
          className={`h-full ${fillColor} transition-all duration-500`}
          style={{ width: `${widthPct}%` }}
        />
      </div>
    </div>
  );
}



function MensGuideShares({ data }) {
  const {
    total_30d = 0,
    total_all_time = 0,
    unique_sharers_30d = 0,
    by_channel_30d = [],
  } = data || {};

  // Don't render the card until there's at least one share — keeps the
  // admin tidy in the early days.
  if (total_all_time === 0) return null;

  return (
    <div
      className="bg-gold/[0.07] border border-gold/30 rounded-2xl p-6 md:p-8"
      data-testid="admin-mens-guide-shares"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-serif-display text-2xl text-ink">
            Men's Guide · <em className="italic text-gold-dark">mate-to-mate shares</em>
          </h3>
          <p className="text-xs text-ink-soft/70 mt-1">
            Who's passing the program on, and how.
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
        <div
          className="bg-cream border border-hairline rounded-xl p-4"
          data-testid="mg-shares-30d"
        >
          <div className="font-serif-display text-3xl text-ink tabular-nums">
            {total_30d.toLocaleString()}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-ink-soft/70">
            Shares · last 30d
          </div>
        </div>
        <div
          className="bg-cream border border-hairline rounded-xl p-4"
          data-testid="mg-shares-unique"
        >
          <div className="font-serif-display text-3xl text-ink tabular-nums">
            {unique_sharers_30d.toLocaleString()}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-ink-soft/70">
            Unique sharers · 30d
          </div>
        </div>
        <div
          className="bg-cream border border-hairline rounded-xl p-4"
          data-testid="mg-shares-all-time"
        >
          <div className="font-serif-display text-3xl text-ink tabular-nums">
            {total_all_time.toLocaleString()}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-ink-soft/70">
            Shares · all time
          </div>
        </div>
      </div>

      {by_channel_30d.length > 0 && (
        <div className="mt-6">
          <div className="eyebrow text-ink-soft/70 mb-3 text-[10px]">
            By channel · last 30d
          </div>
          <ul className="space-y-1.5" data-testid="mg-shares-channels">
            {by_channel_30d.map((c) => (
              <li
                key={c.channel}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-ink-soft capitalize">{c.channel}</span>
                <span className="text-ink tabular-nums">{c.count.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
